import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useCompaniesPageUrlState } from '..';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Databoard, DataboardBody } from '../../../../components/Databoard';
import { DataGrid, DataGridColumn, DataGridColumns, DataGridRows } from '../../../../components/DataGrid';
import { EntityTablePlaceholder } from '../../../../components/entities/EntityTablePlaceholder';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { DisclosureIcon } from '../../../../components/svgs/icons/DisclosureIcon';
import { bs } from '../../../../styles/styles';
import { DefaultCompaniesSort, useCompaniesInput, useCompaniesSearch } from '../CompaniesContext';
import { CompanyRow } from '../CompanyRow';
import { styleSheet } from './styles';

export const CompaniesTable: React.FC = observer(() => {
	const {
		companies,
		sort,
		setSort,
		companiesQuery,
		selectionState,
		selectedCompanyIds,
		excludedCompanyIds,
		setSelectedCompanyIds,
		setExcludedCompanyIds,
		setSelectionState,
	} = useCompaniesSearch();
	const [, setCompanyFilterSearch] = useCompaniesPageUrlState();
	const { setSearch } = useCompaniesInput();

	const onToggleCompanyRow = (company: Api.ICompany) => {
		const currentIndex = selectedCompanyIds.indexOf(company.id);
		const nextSelectedCompanyIds = [...selectedCompanyIds];

		if (
			selectionState === Api.ESelectionState.All ||
			(selectionState === Api.ESelectionState.Some && excludedCompanyIds.length > 0)
		) {
			const nextExcludedCompanyIds = [...excludedCompanyIds];
			if (currentIndex > -1) {
				nextExcludedCompanyIds.push(company.id);
				if (nextExcludedCompanyIds.length === companies.length) {
					setSelectionState(Api.ESelectionState.All);
				} else {
					setSelectionState(Api.ESelectionState.Some);
				}
			} else {
				nextExcludedCompanyIds.splice(currentIndex, 1);
				if (nextExcludedCompanyIds.length === 0) {
					setSelectionState(Api.ESelectionState.None);
				} else {
					setSelectionState(Api.ESelectionState.Some);
				}
			}
			setExcludedCompanyIds(nextExcludedCompanyIds);
		}

		if (currentIndex > -1) {
			nextSelectedCompanyIds.splice(currentIndex, 1);
		} else {
			nextSelectedCompanyIds.push(company.id);
		}
		const isLastSelectedCompany = currentIndex > -1 && selectedCompanyIds.length === 1;
		if (isLastSelectedCompany) {
			setSelectionState(Api.ESelectionState.None);
		} else if (nextSelectedCompanyIds.length === companies.length) {
			setSelectionState(Api.ESelectionState.All);
		} else {
			setSelectionState(Api.ESelectionState.Some);
		}
		setSelectedCompanyIds(nextSelectedCompanyIds);
	};

	const loadMore = () => {
		if (companiesQuery.hasNextPage) {
			companiesQuery.fetchNextPage();
		}
	};

	const onClearButtonClicked = () => {
		setCompanyFilterSearch({
			companySearchProperty: Api.CompanySearchRequestProperty.Name,
			companySearchValue: '',
			companyOrTags: [],
			companyAndTags: [],
			companyNotTags: [],
		});
		setSort(DefaultCompaniesSort);
		setSearch('');
	};

	const onSort = (newSort: Api.ISortDescriptor) => () => {
		setSort(newSort);
	};

	const onSelectAllCheckedChanged = (checked: boolean) => {
		if (checked || selectionState === Api.ESelectionState.Some) {
			setSelectionState(Api.ESelectionState.All);
			setSelectedCompanyIds(companies.map(company => company.id));
		} else {
			setSelectionState(Api.ESelectionState.None);
			setSelectedCompanyIds([]);
		}
	};

	const sortDirectionDesc = sort.sort === 'desc';

	return (
		<Databoard styles={[styleSheet.databoard]}>
			<DataboardBody styles={[styleSheet.databoardBody]} data-scroll-restoration-id='companies-table'>
				<DataGrid>
					<DataGridColumns>
						<DataGridColumn sticky>
							<div className={css(bs.flex, bs.flexRow, bs.itemsCenter, bs.gap4)}>
								<Checkbox
									id='select-all-companies'
									size='lg'
									checked={
										selectionState === Api.ESelectionState.Some
											? 'indeterminate'
											: selectionState === Api.ESelectionState.All
									}
									onCheckedChange={onSelectAllCheckedChanged}
								/>
								<Popover>
									<PopoverTrigger asChild>
										<div className={css(styleSheet.sortTrigger)}>
											Company Name
											<span className={css(styleSheet.sortIcon, sortDirectionDesc && styleSheet.sortIconFlipped)}>
												<DisclosureIcon type='chevron' />
											</span>
										</div>
									</PopoverTrigger>
									<PopoverContent align='start' styles={[styleSheet.sortMenu]} sideOffset={4}>
										<>
											<button
												className={css(styleSheet.sortMenuItem)}
												onClick={onSort({ sort: 'asc', sortBy: 'companyName' })}
											>
												Company Name A-Z
											</button>
											<button
												className={css(styleSheet.sortMenuItem)}
												onClick={onSort({
													sort: 'desc',
													sortBy: 'companyName',
												})}
											>
												Company Name Z-A
											</button>
										</>
									</PopoverContent>
								</Popover>
							</div>
						</DataGridColumn>
						<DataGridColumn sticky>Tags</DataGridColumn>
						<DataGridColumn sticky>Domain</DataGridColumn>
					</DataGridColumns>

					<DataGridRows>
						{companies.length > 0
							? companies.map(company => {
									return (
										<CompanyRow
											company={company}
											selectionState={selectionState}
											selectedCompanyIds={selectedCompanyIds}
											excludedCompanyIds={excludedCompanyIds}
											onSelectToggle={onToggleCompanyRow}
											key={company.id}
										/>
									);
								})
							: null}
					</DataGridRows>
				</DataGrid>
				{!companies.length ? (
					<EntityTablePlaceholder onClearButtonClicked={onClearButtonClicked} type='company' />
				) : null}
				{companiesQuery.hasNextPage && !companiesQuery.isFetchingNextPage && (
					<Waypoint key='load-more-trigger' bottomOffset='-400px' onEnter={loadMore} />
				)}
			</DataboardBody>
		</Databoard>
	);
});
